import { GridColDef } from "@mui/x-data-grid-pro";
import { SWRDataGrid } from "./SWRDataGrid";
import { GridPaginationProps } from "./helpers";
import { HostFavoritePlayer } from "services/models/Host";
import { PlayerNameCell } from "./PlayerNameCell";
import { currencyCol, numberCol } from "utils/datagrid-utils";
import { useFavoritesUpdater, useHostFavoritePlayer } from "services/api/SWRService";
import { Star } from "@mui/icons-material";
import { IconButton, Typography, useTheme } from "@mui/material";
import { useConfirm } from "hooks/ConfirmationDialog";
import { enqueueSnackbar } from "notistack";
import { errorMessage } from "@sanmanuel/smkit-react";
import { SMTooltip } from "components/lib/SMTooltip";
import PlaceIcon from "@mui/icons-material/Place";
import { PLAYER_NAME_MIN_WIDTH } from "constants/tables";
import moment from "moment";
import { useCanAccess } from "@sanmanuel/permissions";
import { AppPermissions } from "constants/permissions";

export const PlayersOfInterestTable = (props: GridPaginationProps) => {
  const response = useHostFavoritePlayer();
  const theme = useTheme();
  const confirm = useConfirm();
  const updater = useFavoritesUpdater();
  const hasStats = useCanAccess(AppPermissions.view.playerStats);

  const confirmUnfollowPlayer = (playerId: number) => {
    confirm({
      description: "Do you want to unfollow this player of interest? It will remove them from this table.",
      confirmationText: "Yes, Unfollow Player",
      confirmationButtonProps: { color: "error" },
      isAsynchronous: true,
      allowClose: true,
    })
      .then(async (completed) => {
        const res = await updater.unfollow(playerId);
        if (res.ok) {
          response.mutate();
          enqueueSnackbar(`Player unfollowed.`, { variant: "success" });
        } else {
          enqueueSnackbar(`Error unfollowing player with ${res.statusText}`, { variant: "error" });
        }

        completed();
      })
      .catch((err) => {
        if (!err) return;
        enqueueSnackbar(errorMessage(err), { variant: "error" });
      });
  };

  const protectedStats: GridColDef<HostFavoritePlayer>[] = [
    {
      field: "adt",
      headerName: "90-Day ADT",
      minWidth: 120,
      ...currencyCol,
    },
    {
      field: "adw",
      headerName: "90-Day ADW",
      minWidth: 120,
      ...currencyCol,
    },
    {
      field: "avt",
      headerName: "AvT",
      ...numberCol((v) => (v > 12 ? "12+" : v)),
    },
  ];

  const baseCols: GridColDef<HostFavoritePlayer>[] = [
    {
      field: "favorite",
      headerName: "",
      type: "singleSelect",
      maxWidth: 50,
      renderCell: ({ row }) => {
        return (
          <SMTooltip title={"Click to unfollow player"}>
            <IconButton onClick={() => confirmUnfollowPlayer(row.clubNumber)}>
              <Star sx={{ color: theme.palette.primary.main }} />{" "}
            </IconButton>
          </SMTooltip>
        );
      },
    },
    {
      field: "fullName",
      headerName: "Player",
      flex: 1,
      minWidth: PLAYER_NAME_MIN_WIDTH,
      renderCell: ({ row }) => (
        <PlayerNameCell {...row}>
          {row.isOnTheFloor && (
            <SMTooltip title="Has card activity within last 2 hours">
              <PlaceIcon color="warning" />
            </SMTooltip>
          )}
        </PlayerNameCell>
      ),
    },
    {
      field: "hostName",
      headerName: "Host",
      minWidth: 150,
    },
    {
      field: "activityTimestamp",
      headerName: "Recent Activity",
      type: "dateTime",
      minWidth: 400,
      valueGetter: ({ value }) => new Date(value),
      renderCell: ({ row }) => {
        if (row.activityLocation === null) return <Typography>N/A</Typography>;
        const [location, gameName] = row.activityLocation.split("\n");

        return (
          <Typography>
            {row.activity} - {moment(row.activityTimestamp).format("MM/DD/YYYY h:mm A")}
            {location != "" && <br />}
            {location}
            <br />
            {gameName}
          </Typography>
        );
      },
    },
  ];

  if (hasStats) {
    baseCols.splice(3, 0, ...protectedStats);
  }

  const columns: GridColDef<HostFavoritePlayer>[] = baseCols;

  return (
    <SWRDataGrid
      initialState={{
        columns: {
          columnVisibilityModel: {
            currentTier: true,
          },
        },
        sorting: {
          sortModel: [{ field: "activityTimestamp", sort: "desc" }],
        },
      }}
      name="players-of-interest"
      response={response}
      columns={columns}
      noRowsMessage="You have no favorited players. You can favorite a player by going to overview page and clicking the star button on the top right corner."
      {...props}
    />
  );
};
